import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { LanguageProvider } from "@/context/LanguageContext";
import { OnboardingProvider } from "@/context/OnboardingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";

import ErrorBoundary from "@/components/ErrorBoundary";
import Welcome from "@/pages/Welcome";
import AuthCallback from "@/pages/AuthCallback";

import OnboardingProfile from "@/pages/onboarding/Profile";
import OnboardingPriorities from "@/pages/onboarding/Priorities";
import OnboardingDeepDive from "@/pages/onboarding/DeepDive";
import OnboardingBirth from "@/pages/onboarding/Birth";

import RevealLoading from "@/pages/reveal/Loading";
import RevealLifeMap from "@/pages/reveal/LifeMap";
import RevealChapter from "@/pages/reveal/Chapter";
import RevealGlimpses from "@/pages/reveal/Glimpses";
import RevealResonance from "@/pages/reveal/Resonance";
import RevealMission from "@/pages/reveal/Mission";

import Today from "@/pages/app/Today";
import Mission from "@/pages/app/Mission";
import Journey from "@/pages/app/Journey";
import Progress from "@/pages/app/Progress";
import Menu from "@/pages/app/Menu";
import Journal from "@/pages/app/Journal";
import CurrentAntardashaRedirect from "@/pages/app/CurrentAntardashaRedirect";
import { lazy, Suspense } from "react";
import CelestialLoader from "@/components/CelestialLoader";

// Lazy-load less-used pages to keep initial bundle small
const Glossary = lazy(() => import("@/pages/app/Glossary"));
const LifeLetterEditor = lazy(() => import("@/pages/app/LifeLetterEditor"));
const Kundli = lazy(() => import("@/pages/app/Kundli"));
const StaticContent = lazy(() => import("@/pages/app/StaticContent"));
const Discovery = lazy(() => import("@/pages/app/Discovery"));
const Resonances = lazy(() => import("@/pages/app/Resonances"));
const PhaseMap = lazy(() => import("@/pages/app/PhaseMap"));
const AntardashaPhaseMap = lazy(() => import("@/pages/app/AntardashaPhaseMap"));
const MahadashaDetail = lazy(() => import("@/pages/app/MahadashaDetail"));
const AntardashaDetail = lazy(() => import("@/pages/app/AntardashaDetail"));
const EditHub = lazy(() => import("@/pages/app/profile/EditHub"));
const EditPersonal = lazy(() => import("@/pages/app/profile/EditPersonal"));
const EditPriorities = lazy(() => import("@/pages/app/profile/EditPriorities"));
const EditBirth = lazy(() => import("@/pages/app/profile/EditBirth"));
const Subscribe = lazy(() => import("@/pages/app/Subscribe"));

const Lazy = ({ children }) => <Suspense fallback={<CelestialLoader />}>{children}</Suspense>;

import OnboardingLifeLetter from "@/pages/onboarding/LifeLetter";
import OnboardingLanguage from "@/pages/onboarding/Language";
const PublicLegal = lazy(() => import("@/pages/PublicLegal"));
const EmailAuth = lazy(() => import("@/pages/EmailAuth"));
const AccountDeletion = lazy(() => import("@/pages/AccountDeletion"));

function RootRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Welcome />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/auth/email" element={<Lazy><EmailAuth /></Lazy>} />

      {/* Public, no-auth legal pages — published URLs for Google Play & Apple App Store */}
      <Route path="/privacy" element={<Lazy><PublicLegal kind="privacy" /></Lazy>} />
      <Route path="/terms" element={<Lazy><PublicLegal kind="terms" /></Lazy>} />
      <Route path="/account-deletion" element={<Lazy><AccountDeletion /></Lazy>} />
      <Route path="/delete-account" element={<Lazy><AccountDeletion /></Lazy>} />

      <Route path="/onboarding/language" element={<ProtectedRoute requireOnboarding={false}><OnboardingLanguage /></ProtectedRoute>} />
      <Route path="/onboarding/profile" element={<ProtectedRoute requireOnboarding={false}><OnboardingProfile /></ProtectedRoute>} />
      <Route path="/onboarding/priorities" element={<ProtectedRoute requireOnboarding={false}><OnboardingPriorities /></ProtectedRoute>} />
      <Route path="/onboarding/deep-dive/:idx" element={<ProtectedRoute requireOnboarding={false}><OnboardingDeepDive /></ProtectedRoute>} />
      <Route path="/onboarding/life-letter" element={<ProtectedRoute requireOnboarding={false}><OnboardingLifeLetter /></ProtectedRoute>} />
      <Route path="/onboarding/birth" element={<ProtectedRoute requireOnboarding={false}><OnboardingBirth /></ProtectedRoute>} />

      <Route path="/reveal/loading" element={<ProtectedRoute requireOnboarding={false}><RevealLoading /></ProtectedRoute>} />
      <Route path="/reveal/life-map" element={<ProtectedRoute requireOnboarding={false}><RevealLifeMap /></ProtectedRoute>} />
      <Route path="/reveal/chapter" element={<ProtectedRoute requireOnboarding={false}><RevealChapter /></ProtectedRoute>} />
      <Route path="/reveal/glimpses" element={<ProtectedRoute requireOnboarding={false}><RevealGlimpses /></ProtectedRoute>} />
      <Route path="/reveal/resonance" element={<ProtectedRoute requireOnboarding={false}><RevealResonance /></ProtectedRoute>} />
      <Route path="/reveal/mission" element={<ProtectedRoute requireOnboarding={false}><RevealMission /></ProtectedRoute>} />

      <Route path="/app" element={<Navigate to="/app/today" replace />} />
      <Route path="/app/today" element={<ProtectedRoute><AppLayout><Today /></AppLayout></ProtectedRoute>} />
      <Route path="/app/mission" element={<ProtectedRoute><AppLayout><Mission /></AppLayout></ProtectedRoute>} />
      <Route path="/app/journey" element={<ProtectedRoute><AppLayout><Journey /></AppLayout></ProtectedRoute>} />
      <Route path="/app/journey/journal" element={<ProtectedRoute><AppLayout><Journal /></AppLayout></ProtectedRoute>} />
      {/* Legacy alias */}
      <Route path="/app/journey/annals" element={<Navigate to="/app/journey/journal" replace />} />
      <Route path="/app/journey/mahadasha-map" element={<ProtectedRoute><AppLayout><Lazy><PhaseMap /></Lazy></AppLayout></ProtectedRoute>} />
      {/* Legacy alias */}
      <Route path="/app/journey/phase-map" element={<Navigate to="/app/journey/mahadasha-map" replace />} />
      <Route path="/app/journey/mahadasha/:index" element={<ProtectedRoute><AppLayout><Lazy><MahadashaDetail /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/journey/antardasha-map" element={<ProtectedRoute><AppLayout><Lazy><AntardashaPhaseMap /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/journey/antardasha-current" element={<ProtectedRoute><AppLayout><CurrentAntardashaRedirect /></AppLayout></ProtectedRoute>} />
      <Route path="/app/journey/antardasha/:index" element={<ProtectedRoute><AppLayout><Lazy><AntardashaDetail /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/journey/discovery" element={<ProtectedRoute><AppLayout><Lazy><Discovery /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/journey/resonances" element={<ProtectedRoute><AppLayout><Lazy><Resonances /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/progress" element={<ProtectedRoute><AppLayout><Progress /></AppLayout></ProtectedRoute>} />
      <Route path="/app/menu" element={<ProtectedRoute><AppLayout><Menu /></AppLayout></ProtectedRoute>} />
      <Route path="/app/glossary" element={<ProtectedRoute><AppLayout><Lazy><Glossary /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/kundli" element={<ProtectedRoute><AppLayout><Lazy><Kundli /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/approach" element={<ProtectedRoute><AppLayout><Lazy><StaticContent kind="approach" /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/privacy" element={<ProtectedRoute><AppLayout><Lazy><StaticContent kind="privacy" /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/terms" element={<ProtectedRoute><AppLayout><Lazy><StaticContent kind="terms" /></Lazy></AppLayout></ProtectedRoute>} />
      <Route path="/app/life-letter" element={<ProtectedRoute><AppLayout><Lazy><LifeLetterEditor /></Lazy></AppLayout></ProtectedRoute>}/>
      <Route path="/app/profile/edit" element={<ProtectedRoute><AppLayout><Lazy><EditHub /></Lazy></AppLayout></ProtectedRoute>}/>
      <Route path="/app/profile/edit/personal" element={<ProtectedRoute><AppLayout><Lazy><EditPersonal /></Lazy></AppLayout></ProtectedRoute>}/>
      <Route path="/app/profile/edit/priorities" element={<ProtectedRoute><AppLayout><Lazy><EditPriorities /></Lazy></AppLayout></ProtectedRoute>}/>
      <Route path="/app/profile/edit/birth" element={<ProtectedRoute><AppLayout><Lazy><EditBirth /></Lazy></AppLayout></ProtectedRoute>}/>
      <Route path="/app/subscribe" element={<ProtectedRoute><AppLayout><Lazy><Subscribe /></Lazy></AppLayout></ProtectedRoute>}/>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ThemeProvider>
          <AuthProvider>
            <OnboardingProvider>
              <BrowserRouter>
                <RootRouter />
                <Toaster position="top-center" />
              </BrowserRouter>
            </OnboardingProvider>
          </AuthProvider>
        </ThemeProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
